import { AbstractMesh, Animation, Color3, PBRMaterial, QuadraticEase, EasingFunction } from '@babylonjs/core';
import { TORUS_COLORS, TORUS_TRANSITION_MS } from '../utils/constants';

export enum TorusState {
  IDLE = 'idle',
  RECORDING = 'recording',
  UPLOADING = 'uploading',
  ERROR = 'error'
}

const STATE_COLORS: Record<TorusState, Color3> = {
  [TorusState.IDLE]: new Color3(TORUS_COLORS.idle.r, TORUS_COLORS.idle.g, TORUS_COLORS.idle.b),
  [TorusState.RECORDING]: new Color3(TORUS_COLORS.recording.r, TORUS_COLORS.recording.g, TORUS_COLORS.recording.b),
  [TorusState.UPLOADING]: new Color3(TORUS_COLORS.uploading.r, TORUS_COLORS.uploading.g, TORUS_COLORS.uploading.b),
  [TorusState.ERROR]: new Color3(TORUS_COLORS.error.r, TORUS_COLORS.error.g, TORUS_COLORS.error.b),
};

const FPS = 30;
const FRAMES = Math.round((TORUS_TRANSITION_MS / 1000) * FPS);
const COLOR_ANIM_NAME = 'torusColorTransition';

/**
 * Smoothly transitions the hollaSphere's emissive color to match the target state.
 * Returns a Promise that resolves when the animation completes.
 */
export function transitionTorusColor(
  mesh: AbstractMesh,
  targetState: TorusState
): Promise<void> {
  const scene = mesh.getScene();

  // Find the material on child meshes (root mesh has no material)
  let material: PBRMaterial | null = null;
  for (const child of mesh.getChildMeshes()) {
    if (child.material && child.material instanceof PBRMaterial) {
      material = child.material;
      break;
    }
  }

  if (!material) {
    console.warn('[TorusState] No PBRMaterial found on mesh');
    return Promise.resolve();
  }

  const fromColor = material.emissiveColor.clone();
  const toColor = STATE_COLORS[targetState];

  // Stop any existing color animation on the material
  scene.stopAnimation(material, COLOR_ANIM_NAME);

  const colorAnim = new Animation(
    COLOR_ANIM_NAME,
    'emissiveColor',
    FPS,
    Animation.ANIMATIONTYPE_COLOR3,
    Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  colorAnim.setKeys([
    { frame: 0, value: fromColor },
    { frame: FRAMES, value: toColor },
  ]);

  // Add easing for smooth transition
  const easing = new QuadraticEase();
  easing.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
  colorAnim.setEasingFunction(easing);

  if (!material.animations) {
    material.animations = [];
  }
  material.animations = material.animations.filter(a => a.name !== COLOR_ANIM_NAME);
  material.animations.push(colorAnim);

  return new Promise<void>((resolve) => {
    scene.beginAnimation(material, 0, FRAMES, false, 1, () => {
      resolve();
    });
  });
}
